import { Component, createRef } from "react";
import { QESButton } from "@app/shared/qes-standards";
import { QESCheckbox } from "../../shared/qes-standards/forms/Checkbox";

import { RoleRestricted, Roles } from "../../user";
import { ProjectionProviderType, ProviderNetworkType } from "../enumerators";
import ProjectService from "../projectService";
import { FlyoutContextOutgoingProps, FlyoutFooter, withFlyoutContext } from "@app/shared/Flyouts";
import NetNewServicingProviderService from "../servicing-provider/NetNewServicingProviderService";

interface DeleteProjectionProviderProps {
  projectId: number;
  projectionProviderProjectIds: number[];
  netNewServicingProviderProjectIds: number[];
  projectionProviderType: ProjectionProviderType;
  providerNetworkType: ProviderNetworkType;
  /** Called after successful removal of projection providers */
  onProjectionProvidersDeleted: (runProjection: boolean) => void;
  wrapperComponentType: WrapperComponentType;
}

interface DeleteProjectionProviderState {
  allLocations: boolean;
  allProjects: boolean;
  allSpecialties: boolean;
  reasonDescription: string;
  reasonName: string;
  runProjectionOnSubmit: boolean;
  status: Status;
}

type Status = "canSubmit" | "removing" | "finished" | "error";

export type WrapperComponentType = "flyoutLink" | "modal";

export class WrappedDeleteProjectionProvider extends Component<DeleteProjectionProviderProps & FlyoutContextOutgoingProps, DeleteProjectionProviderState> {
  private readonly projectService = new ProjectService();
  private readonly netNewServicingProviderService = new NetNewServicingProviderService();

  constructor(props: DeleteProjectionProviderProps & FlyoutContextOutgoingProps) {
    super(props);

    this.state = {
      allLocations: false,
      allProjects: false,
      allSpecialties: false,
      reasonDescription: null,
      reasonName: null,
      runProjectionOnSubmit: true,
      status: "canSubmit",
    };
  }

  public render() {
    const canSubmit = this.state && this.state.status === "canSubmit";
    const multiple = this.props.projectionProviderProjectIds.length > 1 || this.props.netNewServicingProviderProjectIds.length > 1;
    const showProgress = this.state.status !== "canSubmit";

    const containerRef = createRef<HTMLDivElement>();

    return (
      <div ref={containerRef}>
        {/* Progress messages */}
        <div>
          {(showProgress || null) && (
            <>
              {(this.isStatus("removing") || null) && (
                <p className="lead"><i className="fa fa-spinner fa-spin" /> Processing request...</p>
              )}
              {(this.isStatus("finished") || null) && (
                <p className="lead"><i className="fa fa-check" /> Success! Analysis complete...</p>
              )}
              {(this.isStatus("error") || null) && (
                <p className="lead text-danger"><i className="fa fa-exclamation-triangle" /> Error! Failed to remove the {this.projectionTypePastTense.toLowerCase()} provider(s). Review your selections and try again....</p>
              )}
            </>
          )}
        </div>
        <p>
          Submit to no longer
          <strong>
            {` ${this.projectionTypeInfinitiveTense.toLowerCase()}`}
            {` ${this.props.providerNetworkType != ProviderNetworkType.NetNew ? this.props.projectionProviderProjectIds.length : this.props.netNewServicingProviderProjectIds.length}`}
            {` provider${multiple ? "s" : ""} `}
          </strong>
          from the
          <strong>
            {` ${this.providerNetworkTypeDisplay.toLowerCase()} `}
          </strong>
          network...
        </p>
        <div className="form-horizontal" >
          <div className="form-group row mt30">
            <RoleRestricted allowedRoles={[Roles.PROJECTION_PROCESSOR]}>
              <div className="col-12">
                <QESCheckbox
                  checked={!this.state.runProjectionOnSubmit}
                  onChange={(e) => { this.setState({ runProjectionOnSubmit: !e.currentTarget.checked }); }}
                  label="Run Projection Analysis Later"
                  testId="delete-projection__run-later"
                />
              </div>
            </RoleRestricted>
          </div>
        </div>
        {this.props.wrapperComponentType == "flyoutLink" && 
           <FlyoutFooter
              submitButton={<QESButton qesType="primary" onClick={this.handleSubmit} disabled={!canSubmit} qesTestId="delete-projection__submit">Submit</QESButton>}
            />
        }
      </div>
    );
  }

  public handleSubmit = async (): Promise<void> => {
    // TODO CLS - ADDITIONAL VALIDATION AND POTENTIALLY LEVERAGING THE CSS CLASSES FOR FORM ERRORS
    if (this.state.status === "canSubmit") {
      // set the state to indicate we're beginning the 'removal' process
      this.setState((preRemoveState) => ({ ...preRemoveState, status: "removing" }), async () => {
        try {
          // attempt to remove the projection providers
          if (this.props.providerNetworkType != ProviderNetworkType.NetNew)
            await this.projectService.deleteProjectionProviders(this.props.projectId, this.props.projectionProviderProjectIds);
          else if (this.props.netNewServicingProviderProjectIds.length > 0)
            await this.netNewServicingProviderService.updateNetNewIsTargeted(false, this.props.netNewServicingProviderProjectIds)

          // set the state to indicate we've successfully finished the 'removal' process
          this.setState((preFinishState) => ({ ...preFinishState, status: "finished" }), () => {
            // notifiy the consuming component that the projection providers have been removed
            this.props.onProjectionProvidersDeleted(this.state.runProjectionOnSubmit);
            this.props.handleCloseFlyout?.(); //Null check for the cases when the component is called within a modal, where this method will be null or undefined
          });
        } catch (e) {
          // we failed to remove the projection providers
          this.setState((prevState) => ({
            ...prevState,
            status: "error",
          }));
        }
      });
    }
  }

  private isStatus(status: Status): boolean {
    return this.state.status === status;
  }

  private get projectionTypeInfinitiveTense(): string {
    switch (this.props.projectionProviderType) {
      case ProjectionProviderType.ExcludeImpact:
        return "Exclude";
      case ProjectionProviderType.Target:
        return "Target";
      case ProjectionProviderType.Unknown:
        return "Unknown";
    }
  }

  private get projectionTypePastTense(): string {
    switch (this.props.projectionProviderType) {
      case ProjectionProviderType.ExcludeImpact:
        return "Excluded";
      case ProjectionProviderType.Target:
        return "Targeted";
      case ProjectionProviderType.Unknown:
        return "Unknown";
    }
  }

  private get providerNetworkTypeDisplay(): string {
    switch (this.props.providerNetworkType) {
      case ProviderNetworkType.Market:
        return "Market";
      case ProviderNetworkType.Network:
        return "Network";
      case ProviderNetworkType.Prospect:
        return "Prospect";
      default:
        return "";
    }
  }
}

const DeleteProjectionProvider = withFlyoutContext(WrappedDeleteProjectionProvider);
export default DeleteProjectionProvider;
